import * as actionTypes from '../../constants/actionTypes';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { getCurrentUtcTime } from '../../lib/DateUtils';
import { userSchema, quarantineSchema, userSettingsSchema } from '../../schemas';
import { getCurrentOrg } from '../../reducers/selectors';
import { GET, POST, PUT, DELETE } from '../../constants/requestMethods';
import { isEmpty, isNil } from 'lodash';

export const loadUser =
  (id = null) =>
  (dispatch) => {
    const schema = id ? userSchema : [userSchema];
    const endpoint = id ? `/users/${id}` : `/users`;
    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_USER_REQUEST,
          actionTypes.GET_USER_SUCCESS,
          actionTypes.GET_USER_FAILURE,
        ],
        endpoint,
        schema,
        requestMethod: GET,
      },
    });
  };

export const loadUserQuarantine = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_QUARANTINE_REQUEST,
        actionTypes.GET_QUARANTINE_SUCCESS,
        actionTypes.GET_QUARANTINE_FAILURE,
      ],
      endpoint: `/users/${id}/quarantine_settings`,
      schema: quarantineSchema,
      requestMethod: GET,
      ignoreErrors: true,
    },
  });
};

export const saveUserQuarantine = (id, quarantineConfig, isNew) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.SAVE_QUARANTINE_REQUEST,
        actionTypes.SAVE_QUARANTINE_SUCCESS,
        actionTypes.SAVE_QUARANTINE_FAILURE,
      ],
      endpoint: `/users/${id}/quarantine_settings`,
      payload: quarantineConfig,
      schema: quarantineSchema,
      requestMethod: isNew ? POST : PUT,
    },
  });
};

export const deleteUserQuarantineConfig = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_QUARANTINE_REQUEST,
        actionTypes.DELETE_QUARANTINE_SUCCESS,
        actionTypes.DELETE_QUARANTINE_FAILURE,
      ],
      endpoint: `/users/${id}/quarantine_settings`,
      schema: quarantineSchema,
      requestMethod: DELETE,
      requestInfo: {
        id,
      },
    },
  });
};

export const deleteUser = (id) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_USER_REQUEST,
        actionTypes.DELETE_USER_SUCCESS,
        actionTypes.DELETE_USER_FAILURE,
      ],
      endpoint: `/users/${id}`,
      requestMethod: DELETE,
      schema: {},
    },
  });
};

export const deactivateUser = (members) => (dispatch, getState) => {
  const org = getCurrentOrg(getState());
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DEACTIVATE_USER_REQUEST,
        actionTypes.DEACTIVATE_USER_SUCCESS,
        actionTypes.DEACTIVATE_USER_FAILURE,
      ],
      endpoint: `/orgs/${org.id}/members/deactivate`,
      requestMethod: PUT,
      schema: null,
      payload: {
        members,
      },
      requestInfo: {
        orgId: org.id,
      },
    },
  });
};

export const activateUser = (members) => (dispatch, getState) => {
  const org = getCurrentOrg(getState());
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.ACTIVATE_USER_REQUEST,
        actionTypes.ACTIVATE_USER_SUCCESS,
        actionTypes.ACTIVATE_USER_FAILURE,
      ],
      endpoint: `/orgs/${org.id}/members/activate`,
      requestMethod: PUT,
      schema: null,
      payload: {
        members,
      },
      requestInfo: {
        orgId: org.id,
      },
    },
  });
};

export const startEditUser = (key, value) => ({
  type: actionTypes.START_EDIT_USER,
});

export const cancelEditUser = (key, value) => ({
  type: actionTypes.CANCEL_EDIT_USER,
});

export const updateEditUser = (key, value) => ({
  type: actionTypes.UPDATE_EDIT_USER,
  key,
  value,
});

export const clearNewCredentialForQuarantine = () => ({
  type: actionTypes.CLEAR_NEW_CRED_FOR_QUARANTINE,
});

export const acceptTOS = (id) => (dispatch) => {
  const tos_signed_at = getCurrentUtcTime();
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.USER_ACCEPT_TOS_REQUEST,
        actionTypes.USER_ACCEPT_TOS_SUCCESS,
        actionTypes.USER_ACCEPT_TOS_FAILURE,
      ],
      endpoint: `users/${id}`,
      schema: userSchema,
      requestMethod: PUT,
      payload: {
        tos_signed_at,
      },
    },
  });
};

export const updateUserInfo = (id, userInfo) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_USER_INFO_REQUEST,
        actionTypes.UPDATE_USER_INFO_SUCCESS,
        actionTypes.UPDATE_USER_INFO_FAILURE,
      ],
      endpoint: `/users/${id}`,
      schema: userSchema,
      requestMethod: PUT,
      payload: userInfo,
    },
  });
};

export const getUserSettings =
  (forceRefresh = false) =>
  (dispatch, getState) => {
    if (forceRefresh === false) {
      const {
        entities: { userSettings },
      } = getState();
      if (!isEmpty(userSettings)) return;
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_USER_SETTINGS_REQUEST,
          actionTypes.GET_USER_SETTINGS_SUCCESS,
          actionTypes.GET_USER_SETTINGS_FAILURE,
        ],
        endpoint: `/user_settings`,
        schema: [userSettingsSchema],
        requestMethod: GET,
      },
    });
  };

export const addUserSettings =
  (settings_type = null, settings = {}) =>
  (dispatch) => {
    if (isNil(settings_type)) return;

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.CREATE_USER_SETTINGS_REQUEST,
          actionTypes.CREATE_USER_SETTINGS_SUCCESS,
          actionTypes.CREATE_USER_SETTINGS_FAILURE,
        ],
        endpoint: `/user_settings`,
        payload: {
          user_settings_type: settings_type,
          settings,
        },
        schema: userSettingsSchema,
        requestMethod: POST,
      },
    });
  };

export const updateUserSettings =
  (id = null, settings = {}) =>
  (dispatch) => {
    if (isNil(id)) return;

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.CREATE_USER_SETTINGS_REQUEST,
          actionTypes.CREATE_USER_SETTINGS_SUCCESS,
          actionTypes.CREATE_USER_SETTINGS_FAILURE,
        ],
        endpoint: `/user_settings/${id}`,
        payload: {
          settings,
        },
        schema: userSettingsSchema,
        requestMethod: PUT,
      },
    });
  };
